import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button, Table, Card, Spinner } from 'react-bootstrap';
import './BillPage.scss';
import dayjs from 'dayjs';
import { ORDER_STATUS_TYPES_MAP,ORDER_STATUS_TYPES, ROLE_TYPES } from '@/constants/constants.js';
import {
  exportBillExcel,
  fetchClientCompanies,
  fetchMonthlyBills,
} from '@/features/orders/billing/billingService.js';

// Unified statuses (0/4/5/6/7 mapped to "视频制作中", status 9 excluded as it's never visible)
const DISPLAY_STATUSES = [
  { value: 0, label: ORDER_STATUS_TYPES_MAP[0] },
  { value: 1, label: ORDER_STATUS_TYPES_MAP[1] },
  { value: 2, label: ORDER_STATUS_TYPES_MAP[2] },
  { value: 3, label: ORDER_STATUS_TYPES_MAP[3] },
  { value: 8, label: ORDER_STATUS_TYPES_MAP[8] },
];

const STATUS_CLASSES = {
  [ORDER_STATUS_TYPES.视频制作中]: 'badge-making', [ORDER_STATUS_TYPES.文案确认中]: 'badge-text', [ORDER_STATUS_TYPES.审核中]: 'badge-review',
  [ORDER_STATUS_TYPES.资料修改中]: 'badge-modify', [ORDER_STATUS_TYPES.已完成]: 'badge-done',
};

const getStatusLabel = (status) => {
  let TmpStatus = status;
  if(status === 4 || status === 5 || status === 6 || status === 7) {
    TmpStatus = 0;
  } else {
    TmpStatus = status;
  }
  return ORDER_STATUS_TYPES_MAP[TmpStatus] || String(TmpStatus);
};

const getStatusClass = (status) => {
   let TmpStatus = status;
  if(status === 4 || status === 5 || status === 6 || status === 7) {
    TmpStatus = 0;
  } else {
    TmpStatus = status;
  }
  return STATUS_CLASSES[TmpStatus] || '';
}

const BillPage = () => {
  const isAdmin = useMemo(() => Number(sessionStorage.getItem('role_id')) === ROLE_TYPES.管理员, []);
  const sessionCompanyName = sessionStorage.getItem('companyName') || '';

  const today = dayjs();
  const firstDay = today.startOf('month');

  const [startDate, setStartDate] = useState(firstDay.format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(today.format('YYYY-MM-DD'));
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [companyNames, setCompanyNames] = useState([]);
  const [companyInput, setCompanyInput] = useState('');
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  // Company dropdown states
  const [companyOptions, setCompanyOptions] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const companyInputRef = useRef(null);
  const companyDropdownRef = useRef(null);
  const debounceTimerRef = useRef(null); // Ref for debouncing API requests

  const dateError =
    startDate && endDate && startDate > endDate
      ? '起始日期不能晚于结束日期'
      : '';
  const canExport =
    startDate && endDate && !dateError && !exporting &&
    (!isAdmin || companyNames.length > 0 || !companyInput.trim());

  // ---- Initialize default selected statuses (exclude "未提交"=9) ----
  useEffect(() => {
    setSelectedStatuses([]);
  }, []);

  // ---- load company list ----
  const loadCompanies = useCallback(async (keyword = '') => {
    if (!isAdmin) return;
    setCompanyLoading(true);
    try {
      const response = await fetchClientCompanies();
      let companies = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          companies = response.data;
        } else if (Array.isArray(response.data.data)) {
          companies = response.data.data;
        } else if (Array.isArray(response.data.rows)) {
          companies = response.data.rows;
        }
      }
      const names = companies
        .map((c) => (typeof c === 'string' ? c : c.companyName || c.company_name || c.name || c.label || ''))
        .filter((n) => n)
        .filter((n) => !keyword || n.toLowerCase().includes(keyword.toLowerCase()));
      const uniqueNames = [...new Set(names)];
      setCompanyOptions(uniqueNames);
    } catch (e) {
      console.error('加载公司列表失败', e);
      setCompanyOptions([]);
    } finally {
      setCompanyLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      loadCompanies();
    }
  }, [isAdmin, loadCompanies]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target) &&
        companyInputRef.current &&
        !companyInputRef.current.contains(event.target)
      ) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // ---- load bills ----
  const loadBills = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const effectiveStatuses = selectedStatuses.length > 0 
        ? selectedStatuses 
        : DISPLAY_STATUSES.map(s => s.value);
        
      const params = { 
        startDate, 
        endDate, 
        statuses: effectiveStatuses.join(',')
      };

      if (isAdmin && companyNames.length > 0) {
        params.companyName = companyNames.join(',');
      }
      const response = await fetchMonthlyBills(params);
      if (response.data && response.data.code === 0) {
        setBills(response.data.data?.rows || []);
      } else {
        setBills([]);
      }
    } catch (e) {
      console.error('加载账单失败', e);
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, companyNames, selectedStatuses, isAdmin]);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  // ---- export ----
const handleExport = async () => {
  if (!canExport) return;
  setExporting(true);
  setError('');
  try {
    const effectiveStatuses = selectedStatuses.length > 0 
      ? selectedStatuses 
      : DISPLAY_STATUSES.map(s => s.value);

    const result = await exportBillExcel({
      startDate,
      endDate,
      companyNames: isAdmin && companyNames.length > 0 ? companyNames : undefined,
      statuses: effectiveStatuses.join(','),
    });

    if (!result.success) {
      if (result.unauthorized) {
        setError(result.message);
        setTimeout(() => {
          sessionStorage.clear();
          window.location.href = '/login';
        }, 1500);
        return;
      }
      setError(result.message);
    }
  } catch (err) {
    setError(err.message || '导出失败，请稍后重试');
  } finally {
    setExporting(false);
  }
};

  const totalPrice = useMemo(
    () => bills.reduce((sum, b) => sum + (Number(b.price) || 0), 0),
    [bills]
  );

  // ---- Status handlers ----
  const handleStatusToggle = (value) => {
    setSelectedStatuses((prev) =>
      prev.includes(value)
        ? prev.filter((s) => s !== value)
        : [...prev, value]
    );
  };

  // ---- Company multi-select handlers ----
  const handleCompanyInputChange = (e) => {
    const value = e.target.value;
    setCompanyInput(value);
    if (isAdmin) {
      // Debounce company lookup API calls
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        loadCompanies(value);
      }, 300);
      setShowCompanyDropdown(true);
    }
  };

  const handleCompanySelect = (name) => {
    if (!companyNames.includes(name)) {
      setCompanyNames((prev) => [...prev, name]);
    }
    setCompanyInput('');
    setShowCompanyDropdown(false);
  };

  const handleCompanyInputFocus = () => {
    if (isAdmin) {
      loadCompanies(companyInput);
      setShowCompanyDropdown(true);
    }
  };

  const handleRemoveCompany = (name) => {
    setCompanyNames((prev) => prev.filter((n) => n !== name));
  };

  return (
    <div className="billPage-container">
      {/* Filter Card */}
      <Card className="filter-card">
        <Card.Body>
          {/* Date Range */}
          <div className="filter-row">
            <label className="filter-label">时间范围：</label>
            <div className="date-range">
              <input
                type="date"
                className="form-control date-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span className="date-separator">~</span>
              <input
                type="date"
                className="form-control date-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Status Multi-select (admin only) */}
          {isAdmin && (
            <div className="filter-row">
              <label className="filter-label">订单状态：</label>
              <div className="status-checkboxes">
                {DISPLAY_STATUSES.map((s) => (
                  <label key={s.value} className="status-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(s.value)}
                      onChange={() => handleStatusToggle(s.value)}
                    />
                    <span>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Company Name with Dropdown + Multi-select */}
          <div className="filter-row company-filter-row">
            <label className="filter-label">公司名称：</label>
            <div className="company-input-wrapper" ref={companyDropdownRef}>
              {isAdmin ? (
                <>
                  <input
                    ref={companyInputRef}
                    type="text"
                    className="form-control company-input"
                    value={companyInput}
                    onChange={handleCompanyInputChange}
                    onFocus={handleCompanyInputFocus}
                    placeholder="不选则导出全部"
                    autoComplete="off"
                  />
                   {/* Selected company tags */}
                  {companyNames.length > 0 && (
                    <div className="company-tags">
                      {companyNames.map((name) => (
                        <span key={name} className="company-tag">
                          {name}
                          <button
                            className="tag-remove"
                            onClick={() => handleRemoveCompany(name)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {showCompanyDropdown && (
                    <div className="company-dropdown">
                      {companyLoading ? (
                        <div className="dropdown-item loading">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">加载中...</span>
                        </div>
                      ) : companyOptions.length > 0 ? (
                        companyOptions.map((name, idx) => (
                          <div
                            key={idx}
                            className={`dropdown-item ${companyNames.includes(name) ? 'selected' : ''}`}
                            onClick={() => handleCompanySelect(name)}
                          >
                            {name}
                            {companyNames.includes(name) && (
                              <span className="check-mark">✓</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item empty">无匹配公司</div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <input
                  type="text"
                  className="form-control company-input readonly"
                  value={sessionCompanyName}
                  disabled
                  readOnly
                />
              )}
            </div>
          </div>

          {/* Date Error */}
          {dateError && <div className="error-message">{dateError}</div>}

          {/* Error Message */}
          {error && !dateError && <div className="error-message">{error}</div>}

          {/* Export Button */}
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={!canExport}
            className="export-btn mt-3"
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                导出中...
              </>
            ) : (
              '导出 Excel'
            )}
          </Button>
        </Card.Body>
      </Card>

      {/* Order List Card */}
      <Card className="order-list-card">
        <Card.Body>
          {/* Summary Bar */}
          <div className="summary-bar">
            <span className="me-3">
              共 <strong>{bills.length}</strong> 笔订单
            </span>
            <span>
              合计金额：<strong>¥{totalPrice.toFixed(2)}</strong>
            </span>
          </div>

          {/* Table */}
          <div className="table-responsive-wrapper">
            <Table striped bordered hover responsive className="bill-table">
              <thead>
                <tr>
                  {isAdmin && <th style={{ width: '16%' }}>公司名称</th>}
                  <th style={{ width: isAdmin ? '14%' : '18%' }}>
                    订单ID
                  </th>
                  <th style={{ width: '12%' }}>视频标准</th>
                  <th style={{ width: '10%' }}>客户姓名</th>
                  <th style={{ width: '10%' }}>业务员姓名</th>
                  {isAdmin && (
                    <th style={{ width: '10%' }}>状态</th>
                  )}
                  <th
                    style={{
                      width: isAdmin ? '10%' : '12%',
                      textAlign: 'right',
                    }}
                  >
                    价格
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={isAdmin ? 7 : 5}
                      className="text-center py-4"
                    >
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2 text-muted">加载中...</span>
                    </td>
                  </tr>
                ) : bills.length > 0 ? (
                  bills.map((bill, idx) => {
                    const status = Number(bill.status);
                    const statusLabel = getStatusLabel(status);
                    const statusClass = getStatusClass(status);
                    return (
                      <tr key={bill.order_id || idx}>
                    {isAdmin && <td>{bill.company_name || ''}</td>}
                    <td className="order-id">
                      {bill.order_id}
                    </td>
                    <td>{bill.video_standard || bill.package_standard || ''}</td>
                    <td>{bill.client_name || bill.customer_name || ''}</td>
                    <td>{bill.sales_person || bill.salesperson || ''}</td>
                    {isAdmin && (
                      <td>
                        <span className={`status-badge ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                    )}
                    <td className="price-col">
                      ¥{Number(bill.price || 0).toFixed(2)}
                    </td>
                  </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={isAdmin ? 7 : 5}
                      className="text-center text-muted py-4"
                    >
                      <i className="bi bi-inbox d-block mb-2" style={{ fontSize: '2rem' }}></i>
                      当前筛选条件下无订单数据
                    </td>
                  </tr>
                )}
              </tbody>
              {!loading && bills.length > 0 && (
                <tfoot>
                  <tr className="summary-row">
                    {isAdmin && <td></td>}
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {isAdmin && <td className="fw-bold">合计</td>}
                    <td className="price-col fw-bold">
                      ¥{totalPrice.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BillPage;
